import { optimizeLumberCutsFFD } from '../cut/optimizeLumberCutsFFD';
import { estimateMaterialRowPrice } from '../pricing/priceEstimator';
import type {
  CutRow,
  DeckCalculationResult,
  DeckConfig,
  MaterialRow,
  SaunaDimensions,
} from '../../engines/types';
import {
  type DeckRect,
  getDeckFootprintRects,
  getDeckOpenEdges,
  measureDeckFootprint,
} from './deckFootprint';

const BOARD_META = {
  '28x120': {
    widthMm: 120,
    gapMm: 5,
    joistCcMm: 600,
    label: '28×120 mm',
    screwLabel: 'Rostfri trallskruv 4.2×55mm',
  },
  '22x95': {
    widthMm: 95,
    gapMm: 5,
    joistCcMm: 450,
    label: '22×95 mm',
    screwLabel: 'Rostfri trallskruv 4.2×45mm',
  },
} as const;

const JOIST_META = {
  '45x145': {
    label: '45×145 mm',
    stockLengthsMm: [3600, 4200, 4800, 5400],
    // The kantbräda covers the joist ends and rim joist: the next trade width
    // at or above the joist height.
    fascia: { label: '22×145 mm', heightMm: 145 },
  },
  '45x170': {
    label: '45×170 mm',
    stockLengthsMm: [4200, 4800, 5400, 6000],
    fascia: { label: '22×195 mm', heightMm: 195 },
  },
} as const;

export const DECK_FASCIA_THICKNESS_MM = 22;

const BEARER_META = {
  label: '45×170 mm',
  stockLengthsMm: [4200, 4800, 5400, 6000],
} as const;

const BOARD_STOCK_LENGTHS_MM = [3600, 4200, 4800, 5400] as const;

/** '45×145 mm' → '45x145', the normalized form buildPurchasePlan keys on. */
function toPurchaseDimension(label: string): string {
  return label.replace(/[×x]/g, 'x').replace(/\s+/g, '').replace(/mm/gi, '');
}

function round2(value: number): number {
  return Number(value.toFixed(2));
}

function describePieceSet(count: number, lengthsMm: number[]): string {
  const uniqueLengths = Array.from(new Set(lengthsMm)).sort((a, b) => a - b);
  if (uniqueLengths.length === 0) {
    return `${count} st`;
  }
  if (uniqueLengths.length === 1) {
    return `${count} st × ${(uniqueLengths[0] / 1000).toFixed(1)} m`;
  }
  return `${count} st • ${(uniqueLengths[0] / 1000).toFixed(1)}–${(uniqueLengths.at(-1)! / 1000).toFixed(1)} m`;
}

function splitCutsToFitStock(cutsMm: number[], stockLengthsMm: readonly number[]): number[] {
  const maxStockLength = Math.max(...stockLengthsMm, 0);
  if (maxStockLength <= 0) {
    return cutsMm;
  }

  return cutsMm.flatMap((cutMm) => {
    if (cutMm <= maxStockLength) {
      return [cutMm];
    }

    const pieces = Math.ceil(cutMm / maxStockLength);
    const basePiece = Math.floor(cutMm / pieces);
    const remainder = cutMm - basePiece * pieces;
    return Array.from({ length: pieces }, (_, index) => basePiece + (index < remainder ? 1 : 0));
  });
}


/**
 * How far the frame runs past the outermost bearer and plint lines. A short
 * utkragning is how a free-standing trall is normally built: the plintar sit
 * hidden under the deck instead of on its edge, and on the sauna side the
 * deck's own row stands clear of the sauna's plintar rather than right next
 * to them. Capped for small decks so the bearers never cross.
 */
export const DECK_SUPPORT_CANTILEVER_M = 0.35;

/** Two plintar on one bearer line closer than this are one support cast twice. */
const MIN_PIER_SPACING_M = 0.45;

/** A shared plint must sit this far inside the rect to be under its bearer. */
const SHARED_PIER_EDGE_MARGIN_M = 0.05;

function round3(value: number): number {
  return Number(value.toFixed(3));
}

function supportLines(start: number, length: number, count: number): number[] {
  const inset = Math.min(DECK_SUPPORT_CANTILEVER_M, length / 4);
  return Array.from({ length: count }, (_, index) =>
    round3(start + inset + (index * (length - 2 * inset)) / Math.max(1, count - 1)),
  );
}

const MAX_BEARER_SPACING_M = 2;

/**
 * Where one footprint rect's bearers run (z, deck-local) and where each
 * bearer's plintar stand along it (x). Spans stay ≤ 2 m.
 *
 * `sharedBearerZ` are the lines earlier rects already laid. An L or wrap arm
 * overlapping the main deck snaps a nearby line onto one of those, so the
 * two frames can share the plint where they meet instead of standing a
 * second one a few decimetres off the first.
 */
interface LaidBearer {
  z: number;
  minX: number;
  maxX: number;
}

function planRectSupports(
  rect: DeckRect,
  laid: readonly LaidBearer[],
): { bearerZ: number[]; pierX: number[] } {
  const bearerCount = Math.max(2, Math.ceil(Math.round(rect.depth * 1000) / 2000) + 1);
  const piersPerBearer = Math.ceil(Math.round(rect.width * 1000) / 2000) + 1;
  const zMin = rect.centerZ - rect.depth / 2;
  const zMax = rect.centerZ + rect.depth / 2;
  const xMin = rect.centerX - rect.width / 2;
  const xMax = rect.centerX + rect.width / 2;
  // Only lines of a frame this rect actually overlaps: a rect that merely
  // butts another shares no plint with it.
  const sharedBearerZ = laid
    .filter(
      (line) =>
        Math.min(line.maxX, xMax) - Math.max(line.minX, xMin) > SHARED_PIER_EDGE_MARGIN_M &&
        line.z > zMin + SHARED_PIER_EDGE_MARGIN_M &&
        line.z < zMax - SHARED_PIER_EDGE_MARGIN_M,
    )
    .map((line) => line.z);
  const bearerZ = supportLines(zMin, rect.depth, bearerCount);
  bearerZ.forEach((z, index) => {
    const candidates = sharedBearerZ
      .filter((shared) => Math.abs(shared - z) < MIN_PIER_SPACING_M && !bearerZ.includes(shared))
      .sort((a, b) => Math.abs(a - z) - Math.abs(b - z));
    const snap = candidates[0];
    if (snap === undefined) return;
    const previous = index > 0 ? bearerZ[index - 1] : zMin;
    const next = index < bearerZ.length - 1 ? bearerZ[index + 1] : zMax;
    if (snap - previous <= MAX_BEARER_SPACING_M && next - snap <= MAX_BEARER_SPACING_M) {
      bearerZ[index] = snap;
    }
  });
  return {
    bearerZ,
    pierX: supportLines(rect.centerX - rect.width / 2, rect.width, piersPerBearer),
  };
}

/**
 * Bearer lines and plint positions for every footprint rect, in rect order.
 * Shared by the BOM and the 3D so a plint is always drawn under the bearer
 * it carries.
 */
export function planDeckSupports(
  rects: readonly DeckRect[],
): Array<{ bearerZ: number[]; pierX: number[] }> {
  const laid: LaidBearer[] = [];
  return rects.map((rect) => {
    const plan = planRectSupports(rect, laid);
    for (const z of plan.bearerZ) {
      laid.push({ z, minX: rect.centerX - rect.width / 2, maxX: rect.centerX + rect.width / 2 });
    }
    return plan;
  });
}

export function calculateDeck(
  config: DeckConfig,
  saunaDimensions: SaunaDimensions,
): DeckCalculationResult {
  const boardMeta = BOARD_META[config.boardType];
  const joistMeta = JOIST_META[config.joistDimension];
  const rects = getDeckFootprintRects(config, saunaDimensions);
  const { areaSqm, perimeterM } = measureDeckFootprint(rects);
  const boardPitchMm = boardMeta.widthMm + boardMeta.gapMm;
  const noggingLengthMm = 400;

  const boardCutsMm: number[] = [];
  const joistCutsMm: number[] = [];
  const bearerCutsMm: number[] = [];
  const pierLayout: Array<{ x: number; z: number }> = [];
  let noggingCount = 0;
  let screwCount = 0;

  const supportPlans = planDeckSupports(rects);
  rects.forEach((rect, rectIndex) => {
    const widthMm = Math.round(rect.width * 1000);
    const depthMm = Math.round(rect.depth * 1000);
    const boardCount = Math.max(1, Math.ceil(depthMm / boardPitchMm));
    const joistCount = Math.max(2, Math.ceil(widthMm / boardMeta.joistCcMm) + 1);
    const supports = supportPlans[rectIndex];
    const bearerCount = supports.bearerZ.length;

    // Boards longer than the longest trade length are spliced over a joist —
    // without this, a >5.4 m deck sent an impossible cut into the FFD
    // optimizer, which throws and took the whole calculation down.
    boardCutsMm.push(
      ...splitCutsToFitStock(
        Array.from({ length: boardCount }, () => widthMm),
        BOARD_STOCK_LENGTHS_MM,
      ),
    );
    // Same splice-over-a-pier reasoning as the boards: a wrap-sides deck can
    // put a joist/bearer run past the longest trade length (depth + sideRun
    // up to ~9 m). Splitting here — not just inline for the FFD call below —
    // keeps joistCount/joistLengthMm/bearerCount/bearerLengthMm and the
    // exported `cuts` rows in sync with what buildPurchasePlan/CutPane will
    // actually plan; otherwise those rows report one unsplit 9 m cut and the
    // planner synthesizes a stock length that doesn't exist in the catalog.
    joistCutsMm.push(
      ...splitCutsToFitStock(
        Array.from({ length: joistCount }, () => depthMm),
        joistMeta.stockLengthsMm,
      ),
    );
    bearerCutsMm.push(
      ...splitCutsToFitStock(
        Array.from({ length: bearerCount }, () => widthMm),
        BEARER_META.stockLengthsMm,
      ),
    );

    noggingCount += Math.max(2, (joistCount - 1) * Math.max(1, bearerCount - 1));
    screwCount += boardCount * joistCount * 2;

    // Every plint stands under a bearer, so the 3D can seat it in a shoe.
    // An L or wrap arm on a deck wider than the sauna overlaps the main
    // rect; where its row lands beside one already placed ON THE SAME
    // bearer line and under this rect's bearer, that plint carries both
    // frames instead of a second one being cast beside it. A nearby plint
    // on another line cannot carry this bearer, so it never counts.
    const rectMinX = rect.centerX - rect.width / 2;
    const rectMaxX = rect.centerX + rect.width / 2;
    const maxCantilever = Math.min(DECK_SUPPORT_CANTILEVER_M, rect.width / 4) + SHARED_PIER_EDGE_MARGIN_M;
    // Only plintar of EARLIER rects: a narrow rect's own two plintar can
    // stand closer than the merge distance and both are needed.
    const earlierPiers = pierLayout.slice();
    const { pierX } = supports;
    // Standing in for pierX[index], a shared plint must still carry this
    // rect's bearer as planned: under it, with no longer an end overhang
    // than the utkragning and no span over 2 m to its neighbours.
    const canStandIn = (candidateX: number, index: number) =>
      candidateX > rectMinX + SHARED_PIER_EDGE_MARGIN_M &&
      candidateX < rectMaxX - SHARED_PIER_EDGE_MARGIN_M &&
      (index > 0 || candidateX - rectMinX <= maxCantilever) &&
      (index < pierX.length - 1 || rectMaxX - candidateX <= maxCantilever) &&
      (index === 0 || candidateX - pierX[index - 1] <= MAX_BEARER_SPACING_M) &&
      (index === pierX.length - 1 || pierX[index + 1] - candidateX <= MAX_BEARER_SPACING_M);
    for (const z of supports.bearerZ) {
      // One shared plint stands in for at most one of this line's plintar.
      const used = new Set<{ x: number; z: number }>();
      for (const [index, x] of pierX.entries()) {
        const shared = earlierPiers.find(
          (pier) =>
            !used.has(pier) &&
            Math.abs(pier.z - z) < 0.001 &&
            Math.abs(pier.x - x) < MIN_PIER_SPACING_M &&
            canStandIn(pier.x, index),
        );
        if (shared) used.add(shared);
        else pierLayout.push({ x, z });
      }
    }
  });

  // Kantbräda: one run per open deck edge (never against the sauna wall),
  // spliced like the boards when a run exceeds the longest trade length.
  const fasciaMeta = joistMeta.fascia;
  const fasciaCutsMm = splitCutsToFitStock(
    getDeckOpenEdges(config, saunaDimensions).map((edge) =>
      Math.round((edge.to - edge.from) * 1000),
    ),
    BOARD_STOCK_LENGTHS_MM,
  );
  const fasciaCount = fasciaCutsMm.length;
  const fasciaLengthMm = Math.max(...fasciaCutsMm, 0);
  const totalFasciaLpm = round2(fasciaCutsMm.reduce((sum, lengthMm) => sum + lengthMm, 0) / 1000);

  const boardCount = boardCutsMm.length;
  const boardLengthMm = Math.max(...boardCutsMm, 0);
  const totalBoardLpm = round2(boardCutsMm.reduce((sum, lengthMm) => sum + lengthMm, 0) / 1000);
  const joistCount = joistCutsMm.length;
  const joistLengthMm = Math.max(...joistCutsMm, 0);
  const bearerCount = bearerCutsMm.length;
  const bearerLengthMm = Math.max(...bearerCutsMm, 0);
  const pierCount = pierLayout.length;
  const totalJoistLpm = round2(joistCutsMm.reduce((sum, lengthMm) => sum + lengthMm, 0) / 1000);
  const totalBearerLpm = round2(bearerCutsMm.reduce((sum, lengthMm) => sum + lengthMm, 0) / 1000);
  const totalNoggingLpm = round2((noggingCount * noggingLengthMm) / 1000);
  const screwPackages = Math.max(1, Math.ceil(screwCount / 250));

  const boardCutPlan = optimizeLumberCutsFFD([
    {
      dimension: `Trallbräda ${boardMeta.label}`,
      cutsMm: boardCutsMm,
      stockLengthsMm: [...BOARD_STOCK_LENGTHS_MM],
    },
    {
      dimension: `Kantbräda ${fasciaMeta.label}`,
      cutsMm: fasciaCutsMm,
      stockLengthsMm: [...BOARD_STOCK_LENGTHS_MM],
    },
    {
      dimension: `Regel ${joistMeta.label}`,
      cutsMm: joistCutsMm,
      stockLengthsMm: [...joistMeta.stockLengthsMm],
    },
    {
      dimension: `Bärlina ${BEARER_META.label}`,
      cutsMm: bearerCutsMm,
      stockLengthsMm: [...BEARER_META.stockLengthsMm],
    },
  ]);

  const materials: MaterialRow[] = [
    {
      material: 'Trallbräda',
      // prices.fukttaligtGolv is a per-m² rule: the quantity string must
      // carry an m² token, otherwise the price estimator falls back to the
      // first numeric match (the piece count) and multiplies m²-price × st.
      dimension: boardMeta.label,
      quantity: `${describePieceSet(boardCount, boardCutsMm)} = ${totalBoardLpm} lpm (${areaSqm} m²)`,
      phase: 'deck',
      priceRuleId: 'prices.fukttaligtGolv',
      materialKey: 'deck.boards',
    },
    {
      material: 'Kantbräda',
      dimension: fasciaMeta.label,
      quantity: `${describePieceSet(fasciaCount, fasciaCutsMm)} = ${totalFasciaLpm} lpm`,
      phase: 'deck',
      priceRuleId: 'prices.kantbrada',
      materialKey: 'deck.fascia',
    },
    {
      material: 'Regel',
      dimension: joistMeta.label,
      quantity: `${describePieceSet(joistCount, joistCutsMm)} = ${totalJoistLpm} lpm`,
      phase: 'deck',
      priceRuleId: 'prices.golvTakBjalkar',
      materialKey: 'deck.joists',
    },
    {
      material: 'Bärlina',
      dimension: BEARER_META.label,
      quantity: `${describePieceSet(bearerCount, bearerCutsMm)} = ${totalBearerLpm} lpm`,
      phase: 'deck',
      priceRuleId: 'prices.barlina',
      materialKey: 'deck.bearers',
    },
    {
      material: 'Betongplint',
      dimension: 'Ø160 mm',
      quantity: `${pierCount} st`,
      phase: 'deck',
      priceRuleId: 'prices.betongplint',
      materialKey: 'deck.piers',
    },
    {
      material: 'Stolpsko / infästning',
      dimension: 'Grundinfästning',
      quantity: `${pierCount} st`,
      phase: 'deck',
      priceRuleId: 'prices.stolpsko',
      materialKey: 'deck.post-shoes',
    },
    {
      material: 'Kortling',
      dimension: joistMeta.label,
      quantity: `${noggingCount} st × ${(noggingLengthMm / 1000).toFixed(1)} m = ${totalNoggingLpm} lpm`,
      phase: 'deck',
      priceRuleId: 'prices.kortlingRegel',
      materialKey: 'deck.nogging',
    },
    {
      material: 'Trallskruv',
      dimension: boardMeta.screwLabel,
      quantity: `${screwPackages} paket`,
      phase: 'deck',
      priceRuleId: 'prices.trallskruv',
      materialKey: 'deck.screws',
    },
  ];

  // purchaseDimension pins each row to its real cross-section: the keyword
  // fallback in dimensionFromCutRow would otherwise file deck joists under
  // the wall-stud 45x95 pile and drop the boards entirely.
  const cuts: CutRow[] = [
    { part: 'Trallbrädor', count: `${boardCount} st`, cutLength: `${boardLengthMm} mm`, materialKey: 'deck.boards', purchaseDimension: toPurchaseDimension(boardMeta.label) },
    { part: 'Kantbrädor', count: `${fasciaCount} st`, cutLength: `${fasciaLengthMm} mm`, materialKey: 'deck.fascia', purchaseDimension: toPurchaseDimension(fasciaMeta.label) },
    { part: 'Reglar', count: `${joistCount} st`, cutLength: `${joistLengthMm} mm`, materialKey: 'deck.joists', purchaseDimension: toPurchaseDimension(joistMeta.label) },
    { part: 'Bärlinor', count: `${bearerCount} st`, cutLength: `${bearerLengthMm} mm`, materialKey: 'deck.bearers', purchaseDimension: toPurchaseDimension(BEARER_META.label) },
    { part: 'Kortlingar', count: `${noggingCount} st`, cutLength: `${noggingLengthMm} mm`, materialKey: 'deck.nogging', purchaseDimension: toPurchaseDimension(joistMeta.label) },
  ];

  const estimatedCostSek = materials.reduce(
    (sum, row) => sum + estimateMaterialRowPrice(row, { mode: 'baseline' }).price,
    0,
  );

  return {
    areaSqm,
    perimeterM,
    boards: {
      count: boardCount,
      lengthMm: boardLengthMm,
      dimensionLabel: boardMeta.label,
      ccMm: boardPitchMm,
      totalLpm: totalBoardLpm,
    },
    fascia: {
      count: fasciaCount,
      lengthMm: fasciaLengthMm,
      dimensionLabel: fasciaMeta.label,
      heightMm: fasciaMeta.heightMm,
      thicknessMm: DECK_FASCIA_THICKNESS_MM,
      totalLpm: totalFasciaLpm,
    },
    joists: {
      count: joistCount,
      lengthMm: joistLengthMm,
      dimensionLabel: joistMeta.label,
      ccMm: boardMeta.joistCcMm,
      totalLpm: totalJoistLpm,
    },
    bearers: {
      count: bearerCount,
      lengthMm: bearerLengthMm,
      dimensionLabel: BEARER_META.label,
      totalLpm: totalBearerLpm,
    },
    piers: {
      count: pierCount,
      dimensionLabel: 'Ø160 mm',
      layout: pierLayout,
    },
    noggings: {
      count: noggingCount,
      lengthMm: noggingLengthMm,
      totalLpm: totalNoggingLpm,
    },
    screws: {
      count: screwCount,
      type: boardMeta.screwLabel,
    },
    materials,
    cuts: [
      ...cuts,
      ...boardCutPlan.map((plan) => ({
        part: `${plan.dimension} inköp`,
        count: `${plan.totalBoardsToBuy} st`,
        cutLength: `${plan.stockLengthMm} mm (${plan.wastePercentage} spill)`,
      })),
    ],
    estimatedCostSek,
  };
}
